import React, { useEffect, useState, useMemo } from "react";
import { http, API, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Calendar, Users, Briefcase, Plane, Clock, CheckCircle2, XCircle, ExternalLink, Copy, AlertCircle, Trash2, Ban, Filter, ArrowDownUp, Shield, UserPlus, KeyRound } from "lucide-react";
import { useAuth } from "../lib/auth";

function StatusBadge({ status }) {
  const map = {
    pending: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
    confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
    paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    unpaid: "bg-white/10 text-white/70 border-white/20",
    refunded: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    refund_pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    pending_: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  };
  const label = status === "refund_pending" ? "REFUND PENDING" : status;
  return <span className={`text-[10px] px-2 py-1 rounded-full border uppercase tracking-widest ${map[status] || "bg-white/10 text-white/70 border-white/20"}`}>{label || "-"}</span>;
}

function BookingDetailsDialog({ booking, onClose, onStatus, onAdminCancel, onAdminDelete }) {
  if (!booking) return null;
  const b = booking;
  const fare = b.fare || {};
  const items = fare.items || [];
  return (
    <Dialog open={!!booking} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A1128] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <DialogTitle className="font-heading text-2xl">
                Booking <span className="font-mono text-[#D4AF37]">{b.reference}</span>
              </DialogTitle>
              <DialogDescription className="text-white/60 mt-1">
                Created {b.created_at?.slice(0, 16).replace("T", " ")}
              </DialogDescription>
            </div>
            <div className="flex gap-2">
              <StatusBadge status={b.status} />
              <StatusBadge status={b.payment_status} />
            </div>
          </div>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-2">
          <section>
            <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">Customer</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <Users className="w-4 h-4 mt-0.5 text-white/40 shrink-0" />
                <span data-testid="detail-customer-name" className="font-medium">{b.customer_name || "—"}</span>
              </div>
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 text-white/40 shrink-0" />
                {b.customer_email ? (
                  <a data-testid="detail-customer-email" href={`mailto:${b.customer_email}`} className="text-[#D4AF37] hover:underline break-all">{b.customer_email}</a>
                ) : <span className="text-white/40">—</span>}
              </div>
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 text-white/40 shrink-0" />
                {b.customer_phone ? (
                  <a data-testid="detail-customer-phone" href={`tel:${b.customer_phone}`} className="text-[#D4AF37] hover:underline">{b.customer_phone}</a>
                ) : <span className="text-white/40">—</span>}
              </div>
            </div>
          </section>

          <section>
            <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">Trip</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 mt-0.5 text-white/40 shrink-0" />
                <span>{b.date} · {b.time}</span>
              </div>
              <div className="flex items-start gap-2">
                <Briefcase className="w-4 h-4 mt-0.5 text-white/40 shrink-0" />
                <span>{b.passengers} passengers · {b.luggage} luggage</span>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 text-white/40 shrink-0" />
                <span>{b.trip_type === "return" ? `Return (${b.return_date || ""} ${b.return_time || ""})` : "One-way"}</span>
              </div>
              {b.airport_code && (
                <div className="flex items-start gap-2">
                  <Plane className="w-4 h-4 mt-0.5 text-white/40 shrink-0" />
                  <span>{b.airport_code} — {b.arrival_type || "international"}</span>
                </div>
              )}
            </div>
          </section>
        </div>

        <section className="mt-4">
          <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">Route</div>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <div className="mt-1 w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
              <div className="flex-1">
                <div className="text-white/50 text-xs uppercase tracking-widest mb-0.5">Pickup</div>
                <div data-testid="detail-pickup" className="text-white/95">{b.pickup?.label || "—"}</div>
              </div>
            </div>
            <div className="ml-1 border-l border-dashed border-white/20 h-4" />
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-[#D4AF37] shrink-0" />
              <div className="flex-1">
                <div className="text-white/50 text-xs uppercase tracking-widest mb-0.5">Destination</div>
                <div data-testid="detail-destination" className="text-white/95">{b.destination?.label || "—"}</div>
              </div>
            </div>
          </div>
          {(fare.distance_km || fare.duration_min) && (
            <div className="mt-3 text-xs text-white/50">
              {fare.distance_km} km · {Math.round(fare.duration_min || 0)} min
            </div>
          )}
        </section>

        {b.notes && (
          <section className="mt-4">
            <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-2">Notes</div>
            <p className="text-sm text-white/80 bg-[#040814] border border-white/10 rounded-md p-3">{b.notes}</p>
          </section>
        )}

        <section className="mt-4">
          <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3 flex items-center justify-between">
            <span>Email notifications</span>
            <div className="flex gap-1">
              {b.payment_status !== "paid" && (
                <Button data-testid="mark-paid" onClick={async () => {
                  if (!window.confirm("Manually mark this booking as paid? This will send confirmation emails.")) return;
                  try { await http.post(`/admin/bookings/${b.reference}/mark-paid`); toast.success("Marked paid · emails dispatching"); onStatus(b.reference, "confirmed"); }
                  catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                }} className="h-7 px-3 text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/30">Mark paid</Button>
              )}
              {b.payment_status === "paid" && (
                <Button data-testid="resend-notif" onClick={async () => {
                  try { await http.post(`/admin/bookings/${b.reference}/resend-notifications`); toast.success("Re-sent"); onStatus(b.reference, b.status); }
                  catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                }} className="h-7 px-3 text-[10px] uppercase tracking-widest bg-white/5 hover:bg-white/10 border border-white/15">Resend emails</Button>
              )}
            </div>
          </div>
          {b.payment_status !== "paid" && (
            <p className="text-xs text-white/40 mb-2">Confirmation emails are only sent after payment is confirmed.</p>
          )}
          <div className="rounded-md border border-white/10 bg-[#040814] divide-y divide-white/5">
            {[
              ["email_customer", "Customer email", b.customer_email],
              ["email_admin", "Admin email", null],
            ].map(([k, label, recipient]) => {
              const n = (b.notifications || {})[k] || {};
              return (
                <div key={k} data-testid={`notif-${k}`} className="px-3 py-2.5 flex justify-between gap-3 items-baseline text-sm">
                  <div className="text-white/85">
                    {label}
                    {recipient && <span className="text-white/40 text-xs ml-2">→ {recipient}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {n.sent ? (
                      <span className="text-emerald-300 text-xs flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> Sent</span>
                    ) : n.error ? (
                      <span className="text-red-300 text-xs flex items-center gap-1" title={n.error}><span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" /> Failed</span>
                    ) : (
                      <span className="text-white/40 text-xs flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white/30 inline-block" /> Not sent</span>
                    )}
                    {n.at && (
                      <span className="text-white/40 text-xs tabular-nums">{n.at.slice(0, 16).replace("T", " ")}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-4">
          <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">Charges</div>
          <div className="rounded-md border border-white/10 bg-[#040814] divide-y divide-white/5">
            {items.length === 0 ? (
              <div className="p-3 text-white/50 text-sm">No itemized breakdown stored on this booking.</div>
            ) : items.map((it, i) => (
              <div key={i} className="px-3 py-2 flex justify-between items-baseline gap-3 text-sm">
                <div className="text-white/85">
                  {it.label}
                  {it.detail && <span className="text-white/40 text-xs ml-2">{it.detail}</span>}
                </div>
                <div className={`tabular-nums ${it.amount < 0 ? "text-emerald-300" : "text-white"}`}>
                  {it.amount < 0 ? "−" : ""}€{Math.abs(it.amount).toFixed(2)}
                </div>
              </div>
            ))}
            <div className="px-3 py-3 flex justify-between items-baseline bg-[#D4AF37]/5">
              <span className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Total Amount</span>
              <span className="font-heading text-2xl text-gold-gradient">€{fare.total?.toFixed(2)}</span>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-white/10 bg-white/[0.03] p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs uppercase tracking-[0.18em] text-[#D4AF37]">Invoice</h4>
            {b.invoice_number ? (
              <span className="text-xs px-2 py-1 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] font-mono">#{b.invoice_number}</span>
            ) : (
              <span className="text-xs text-white/40">Not yet generated</span>
            )}
          </div>
          <InvoiceActions booking={b} onChanged={(updated) => onStatus && onStatus(updated.reference, updated.status)} />
        </section>

        <div className="flex gap-2 mt-6 justify-end">
          {b.status !== "confirmed" && b.status !== "cancelled" && (
            <Button data-testid="detail-confirm" onClick={() => onStatus(b.reference, "confirmed")}
              className="bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30">
              Confirm
            </Button>
          )}
          {b.status !== "cancelled" && onAdminCancel && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="detail-cancel" className="bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30">
                  <Ban className="w-3.5 h-3.5 mr-1.5" /> Cancel booking
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#0A1128] border-white/10 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-heading text-xl">Are you sure you want to cancel this booking?</AlertDialogTitle>
                  <AlertDialogDescription className="text-white/70">
                    Reference <span className="font-mono text-[#D4AF37]">{b.reference}</span>
                    {b.payment_status === "paid" ? (
                      <span className="block mt-2 text-amber-300">
                        Paid booking — a Mollie refund of <b>€{fare.total?.toFixed(2)}</b> will be created automatically.
                      </span>
                    ) : (
                      <span className="block mt-2 text-white/60">No refund needed. The customer will receive a cancellation email.</span>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5">Keep booking</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onAdminCancel(b.reference)}
                    className="bg-red-500 hover:bg-red-600 text-white">Yes, cancel</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {onAdminDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="detail-delete" className="bg-white/5 hover:bg-white/15 text-white/70 border border-white/15">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#0A1128] border-white/10 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-heading text-xl">Permanently delete this booking?</AlertDialogTitle>
                  <AlertDialogDescription className="text-white/70">
                    <span className="font-mono text-[#D4AF37]">{b.reference}</span> will be removed. This cannot be undone and does NOT trigger a refund.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5">Keep</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onAdminDelete(b.reference)}
                    className="bg-red-500 hover:bg-red-600 text-white">Delete permanently</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button data-testid="detail-close" onClick={onClose} className="bg-white/5 hover:bg-white/10 border border-white/15">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceActions({ booking, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [localInvoiceNumber, setLocalInvoiceNumber] = useState(booking?.invoice_number || null);

  useEffect(() => { setLocalInvoiceNumber(booking?.invoice_number || null); }, [booking?.invoice_number]);

  const isPaid = (booking?.payment_status || "").toLowerCase() === "paid";
  const hasInvoice = !!localInvoiceNumber;

  const generate = async () => {
    try {
      setBusy(true);
      const { data } = await http.post(`/admin/bookings/${booking.reference}/invoice/generate`);
      setLocalInvoiceNumber(data.invoice_number);
      toast.success(`Invoice ${data.invoice_number} generated`);
      if (onChanged) onChanged({ ...booking, invoice_number: data.invoice_number });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const download = () => {
    // Use a temp anchor with credentials to authenticate via cookie
    window.open(`${API}/admin/bookings/${booking.reference}/invoice.pdf`, "_blank");
  };

  const resend = async () => {
    try {
      setBusy(true);
      const { data } = await http.post(`/admin/bookings/${booking.reference}/invoice/resend`);
      toast.success(`Invoice ${data.invoice_number} email sent to ${data.to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!isPaid) {
    return (
      <div className="text-xs text-white/40">
        Invoice will be generated automatically after the booking is paid.
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {!hasInvoice && (
        <Button data-testid={`generate-invoice-${booking.reference}`} disabled={busy} onClick={generate}
          className="h-9 px-3 text-xs bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814] font-semibold">
          {busy ? "Generating…" : "Generate invoice"}
        </Button>
      )}
      {hasInvoice && (
        <>
          <Button data-testid={`download-invoice-${booking.reference}`} onClick={download}
            className="h-9 px-3 text-xs bg-[#D4AF37]/15 text-[#D4AF37] hover:bg-[#D4AF37]/25 border border-[#D4AF37]/30">
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Download PDF
          </Button>
          <Button data-testid={`resend-invoice-${booking.reference}`} disabled={busy} onClick={resend}
            className="h-9 px-3 text-xs bg-white/10 hover:bg-white/20 text-white border border-white/15">
            <Mail className="w-3.5 h-3.5 mr-1.5" /> Resend invoice email
          </Button>
        </>
      )}
    </div>
  );
}

function BookingsTab() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("created_desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchRef, setSearchRef] = useState("");
  const [busyRef, setBusyRef] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (filter && filter !== "all" && filter !== "custom") params.filter = filter;
      if (filter === "custom" || dateFrom || dateTo) {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      params.sort = sort;
      const { data } = await http.get("/admin/bookings", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [filter, sort, dateFrom, dateTo]); // eslint-disable-line

  const filteredRows = useMemo(() => {
    const q = (searchRef || "").trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((b) =>
      (b.reference || "").toLowerCase().includes(q) ||
      (b.customer_name || "").toLowerCase().includes(q) ||
      (b.customer_email || "").toLowerCase().includes(q) ||
      (b.customer_phone || "").toLowerCase().includes(q)
    );
  }, [rows, searchRef]);

  const setStatus = async (ref, status) => {
    try {
      await http.patch(`/admin/bookings/${ref}`, { status });
      toast.success("Updated");
      await load();
      setSelected((cur) => cur && cur.reference === ref ? { ...cur, status } : cur);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const adminCancel = async (ref) => {
    try {
      setBusyRef(ref);
      const { data } = await http.post(`/admin/bookings/${ref}/cancel`);
      if (data.refund) {
        if (data.refund.ok) {
          toast.success(`Cancelled · refund ${data.refund.status || "queued"} (id ${data.refund.refund_id})`);
        } else {
          toast.warning(`Cancelled — refund needs manual action: ${data.refund.error}`, { duration: 8000 });
        }
      } else {
        toast.success("Booking cancelled · customer email sent");
      }
      await load();
      setSelected(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusyRef(null); }
  };

  const adminDelete = async (ref) => {
    try {
      setBusyRef(ref);
      await http.delete(`/admin/bookings/${ref}`);
      toast.success("Booking deleted");
      await load();
      setSelected(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusyRef(null); }
  };

  const FILTERS = [
    { v: "all", label: "All" },
    { v: "today", label: "Today" },
    { v: "tomorrow", label: "Tomorrow" },
    { v: "week", label: "This week" },
    { v: "custom", label: "Custom date" },
    { v: "paid", label: "Paid" },
    { v: "unpaid", label: "Unpaid" },
    { v: "cancelled", label: "Cancelled" },
    { v: "refunded", label: "Refunded" },
  ];

  return (
    <div className="rounded-xl border border-white/10 bg-[#0A1128]">
      <div className="px-6 py-4 border-b border-white/10 flex flex-wrap gap-3 items-center justify-between">
        <h2 className="font-heading text-xl">All bookings</h2>
        <a data-testid="export-xlsx" href={`${API}/admin/bookings/export`} target="_blank" rel="noreferrer">
          <Button className="h-9 bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Export to Excel</Button>
        </a>
      </div>

      {/* Filter / sort bar */}
      <div className="px-6 py-4 border-b border-white/10 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <Filter className="w-4 h-4 text-white/40" />
          {FILTERS.map((f) => (
            <button
              key={f.v}
              data-testid={`filter-${f.v}`}
              onClick={() => setFilter(f.v)}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest border transition ${
                filter === f.v
                  ? "bg-[#D4AF37] text-[#040814] border-[#D4AF37]"
                  : "bg-transparent text-white/70 border-white/20 hover:border-[#D4AF37]/50"
              }`}
            >{f.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 items-center text-xs">
          {(filter === "custom") && (
            <>
              <Label className="text-white/60">From</Label>
              <Input data-testid="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="bg-[#040814] border-white/20 h-9 w-auto" />
              <Label className="text-white/60">To</Label>
              <Input data-testid="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="bg-[#040814] border-white/20 h-9 w-auto" />
            </>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <ArrowDownUp className="w-3.5 h-3.5 text-white/40" />
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-[#040814] border border-white/20 rounded-md h-9 px-2 text-xs text-white"
            >
              <option value="created_desc">Newest first (created)</option>
              <option value="created_asc">Oldest first (created)</option>
              <option value="date_desc">Trip date — latest</option>
              <option value="date_asc">Trip date — earliest</option>
            </select>
            <Input
              data-testid="search-ref"
              placeholder="Search ref / name / email"
              value={searchRef}
              onChange={(e) => setSearchRef(e.target.value)}
              className="bg-[#040814] border-white/20 h-9 w-56"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-white/50 text-xs uppercase tracking-widest border-b border-white/10">
            <tr>
              <th className="px-4 py-3">Ref</th>
              <th>Customer</th>
              <th>Contact</th>
              <th>Route</th>
              <th>When</th>
              <th>Total</th>
              <th>Status</th>
              <th>Payment</th>
              <th>Invoice</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {filteredRows.map((b) => (
              <tr key={b._id} data-testid={`adm-row-${b.reference}`} className="hover:bg-white/5">
                <td className="px-4 py-3 font-mono text-[#D4AF37] align-top">{b.reference}</td>
                <td className="align-top">{b.customer_name || "—"}</td>
                <td className="align-top text-xs">
                  {b.customer_email && (
                    <div className="text-white/80 break-all"><Mail className="w-3 h-3 inline mr-1 text-white/40" />{b.customer_email}</div>
                  )}
                  {b.customer_phone && (
                    <div data-testid={`adm-phone-${b.reference}`} className="text-white/80 mt-1"><Phone className="w-3 h-3 inline mr-1 text-white/40" />{b.customer_phone}</div>
                  )}
                  {!b.customer_email && !b.customer_phone && <span className="text-white/30">—</span>}
                </td>
                <td className="max-w-[280px] align-top text-xs">
                  <div className="text-white/85 truncate" title={b.pickup?.label}>{b.pickup?.label}</div>
                  <div className="text-white/50 truncate" title={b.destination?.label}>→ {b.destination?.label}</div>
                </td>
                <td className="align-top whitespace-nowrap">{b.date}<div className="text-white/50">{b.time}</div></td>
                <td className="text-[#D4AF37] font-semibold align-top whitespace-nowrap">€{b.fare?.total?.toFixed(2)}</td>
                <td className="align-top"><StatusBadge status={b.status} /></td>
                <td className="align-top"><StatusBadge status={b.payment_status} /></td>
                <td className="align-top text-xs">
                  {b.invoice_number ? (
                    <a data-testid={`row-invoice-link-${b.reference}`}
                      href={`${API}/admin/bookings/${b.reference}/invoice.pdf`} target="_blank" rel="noreferrer"
                      className="font-mono text-[#D4AF37] hover:underline">#{b.invoice_number}</a>
                  ) : (
                    <span className="text-white/30">—</span>
                  )}
                </td>
                <td className="pr-4 align-top">
                  <div className="flex flex-col gap-1 min-w-[150px]">
                    <Button data-testid={`view-details-${b.reference}`} onClick={() => setSelected(b)}
                      className="h-7 px-3 text-xs bg-[#D4AF37]/15 text-[#D4AF37] hover:bg-[#D4AF37]/25 border border-[#D4AF37]/30 whitespace-nowrap">
                      View Details
                    </Button>
                    <div className="flex gap-1">
                      {b.status !== "confirmed" && b.status !== "cancelled" && (
                        <Button data-testid={`confirm-${b.reference}`} className="h-7 px-2 text-xs bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                          onClick={() => setStatus(b.reference, "confirmed")}>Confirm</Button>
                      )}
                      {b.status !== "cancelled" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button data-testid={`adm-cancel-${b.reference}`} disabled={busyRef === b.reference}
                              className="h-7 px-2 text-xs bg-red-500/15 text-red-300 hover:bg-red-500/25">
                              <Ban className="w-3 h-3 mr-1" /> Cancel
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#0A1128] border-white/10 text-white">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-heading text-xl">Are you sure you want to cancel this booking?</AlertDialogTitle>
                              <AlertDialogDescription className="text-white/70">
                                Reference <span className="font-mono text-[#D4AF37]">{b.reference}</span>
                                {b.payment_status === "paid" ? (
                                  <span className="block mt-2 text-amber-300">
                                    This booking is <b>paid</b>. A Mollie refund of <b>€{b.fare?.total?.toFixed(2)}</b> will be created automatically.
                                  </span>
                                ) : (
                                  <span className="block mt-2 text-white/60">No refund needed (not paid). The customer will receive a cancellation email.</span>
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid={`cancel-confirm-no-${b.reference}`} className="bg-transparent border-white/20 text-white hover:bg-white/5">Keep booking</AlertDialogCancel>
                              <AlertDialogAction data-testid={`cancel-confirm-yes-${b.reference}`} onClick={() => adminCancel(b.reference)}
                                className="bg-red-500 hover:bg-red-600 text-white">Yes, cancel</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button data-testid={`adm-delete-${b.reference}`} disabled={busyRef === b.reference}
                            className="h-7 px-2 text-xs bg-white/5 text-white/60 hover:bg-white/15">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#0A1128] border-white/10 text-white">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-heading text-xl">Permanently delete this booking?</AlertDialogTitle>
                            <AlertDialogDescription className="text-white/70">
                              <span className="font-mono text-[#D4AF37]">{b.reference}</span> will be removed from the database.
                              This action cannot be undone and does <b>not</b> trigger a refund.
                              {b.payment_status === "paid" && (
                                <span className="block mt-2 text-amber-300">⚠️ This booking is paid. Cancel it first to issue a refund, then delete if needed.</span>
                              )}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`del-confirm-no-${b.reference}`} className="bg-transparent border-white/20 text-white hover:bg-white/5">Keep</AlertDialogCancel>
                            <AlertDialogAction data-testid={`del-confirm-yes-${b.reference}`} onClick={() => adminDelete(b.reference)}
                              className="bg-red-500 hover:bg-red-600 text-white">Delete permanently</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
            {!filteredRows.length && <tr><td colSpan={10} className="px-4 py-10 text-center text-white/50">No bookings match these filters.</td></tr>}
          </tbody>
        </table>
      </div>

      <BookingDetailsDialog booking={selected} onClose={() => setSelected(null)} onStatus={setStatus} onAdminCancel={adminCancel} onAdminDelete={adminDelete} />
    </div>
  );
}

function DriversTab() {
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", license_plate: "", vehicle: "", active: true });

  const load = async () => {
    try { const { data } = await http.get("/admin/drivers"); setDrivers(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await http.post("/admin/drivers", form);
      toast.success("Driver added");
      setForm({ name: "", phone: "", email: "", license_plate: "", vehicle: "", active: true });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete driver?")) return;
    await http.delete(`/admin/drivers/${id}`); load();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={add} className="rounded-xl border border-white/10 bg-[#0A1128] p-6 space-y-3">
        <h3 className="font-heading text-lg mb-2">Add driver</h3>
        <Input data-testid="drv-name" required placeholder="Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-[#040814] border-white/20 h-11" />
        <Input data-testid="drv-phone" required placeholder="Phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-[#040814] border-white/20 h-11" />
        <Input data-testid="drv-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-[#040814] border-white/20 h-11" />
        <Input data-testid="drv-plate" placeholder="License plate" value={form.license_plate} onChange={(e) => setForm({...form, license_plate: e.target.value})} className="bg-[#040814] border-white/20 h-11" />
        <Input data-testid="drv-vehicle" placeholder="Vehicle (Mercedes E-Class, ...)" value={form.vehicle} onChange={(e) => setForm({...form, vehicle: e.target.value})} className="bg-[#040814] border-white/20 h-11" />
        <Button data-testid="drv-add" className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Add</Button>
      </form>
      <div className="lg:col-span-2 rounded-xl border border-white/10 bg-[#0A1128] divide-y divide-white/10">
        {drivers.map((d) => (
          <div key={d._id} className="p-5 flex items-center justify-between">
            <div>
              <div className="font-heading text-lg">{d.name}</div>
              <div className="text-white/60 text-sm">{d.phone} · {d.vehicle} {d.license_plate && `· ${d.license_plate}`}</div>
            </div>
            <Button data-testid={`del-drv-${d._id}`} className="bg-red-500/15 text-red-300 hover:bg-red-500/25 h-8" onClick={() => remove(d._id)}>Delete</Button>
          </div>
        ))}
        {!drivers.length && <div className="p-10 text-center text-white/50">No drivers yet.</div>}
      </div>
    </div>
  );
}

function PricingTab() {
  const [p, setP] = useState(null);
  const [airports, setAirports] = useState([]);
  const load = async () => {
    const a = await http.get("/pricing"); setP(a.data);
    const b = await http.get("/airports"); setAirports(b.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await http.put("/admin/pricing", p); toast.success("Pricing saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveAirport = async (a) => {
    try { await http.put(`/admin/airports/${a.code}`, a); toast.success(`${a.code} saved`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!p) return null;
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6">
        <h3 className="font-heading text-xl mb-4">General pricing</h3>
        <div className="space-y-3">
          <div><Label>Base fare (€)</Label><Input data-testid="price-base" type="number" step="0.1" value={p.base_fare} onChange={(e) => setP({...p, base_fare: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Per km (€)</Label><Input data-testid="price-km" type="number" step="0.1" value={p.per_km} onChange={(e) => setP({...p, per_km: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Per minute travel (€)</Label><Input data-testid="price-min" type="number" step="0.05" value={p.per_min} onChange={(e) => setP({...p, per_min: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Per minute waiting (€)</Label><Input data-testid="price-min-wait" type="number" step="0.05" value={p.per_min_wait ?? 1.0} onChange={(e) => setP({...p, per_min_wait: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Free waiting minutes (included)</Label><Input data-testid="price-free-wait" type="number" step="1" min="0" value={p.free_waiting_min ?? 5} onChange={(e) => setP({...p, free_waiting_min: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Standby rate (€/min, return-trip waiting)</Label><Input data-testid="price-standby" type="number" step="0.05" min="0" value={p.standby_per_min ?? 0.30} onChange={(e) => setP({...p, standby_per_min: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Night surcharge (%)</Label><Input data-testid="price-night" type="number" step="1" value={p.night_surcharge_pct} onChange={(e) => setP({...p, night_surcharge_pct: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>Return discount (%)</Label><Input data-testid="price-return" type="number" step="1" value={p.return_discount_pct} onChange={(e) => setP({...p, return_discount_pct: parseFloat(e.target.value)})} className="bg-[#040814] border-white/20 h-11" /></div>

          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
            <Label className="text-amber-300">Minimum booking amount (€)</Label>
            <Input
              data-testid="price-min-booking"
              type="number"
              step="1"
              min="0"
              value={p.min_booking_amount ?? 55}
              onChange={(e) => setP({...p, min_booking_amount: parseFloat(e.target.value)})}
              className="bg-[#040814] border-white/20 h-11 mt-1"
            />
            <p className="text-xs text-white/50 mt-2">
              If the calculated fare is below this amount, customers see a message asking them to contact you by phone or WhatsApp and the Pay button is disabled.
            </p>
          </div>

          <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 space-y-3">
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <div>
                <div className="text-sm font-medium text-white">Include 21% VAT / BTW</div>
                <div className="text-xs text-white/50 mt-0.5">When on, VAT is added as a line on top of the subtotal in fares, emails and Mollie payments.</div>
              </div>
              <input
                data-testid="price-vat-enabled"
                type="checkbox"
                checked={!!p.vat_enabled}
                onChange={(e) => setP({...p, vat_enabled: e.target.checked})}
                className="w-5 h-5 accent-[#D4AF37] cursor-pointer"
              />
            </label>
            <div className={p.vat_enabled ? "" : "opacity-50 pointer-events-none"}>
              <Label className="text-xs">VAT rate (%)</Label>
              <Input
                data-testid="price-vat-pct"
                type="number"
                step="0.5"
                min="0"
                max="100"
                value={p.vat_pct ?? 21}
                onChange={(e) => setP({...p, vat_pct: parseFloat(e.target.value)})}
                className="bg-[#040814] border-white/20 h-11"
              />
            </div>
          </div>

          <Button data-testid="save-pricing" onClick={save} className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Save</Button>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6">
        <h3 className="font-heading text-xl mb-4">Airport flat rates & waiting</h3>
        <div className="grid grid-cols-12 gap-2 items-center text-[10px] uppercase tracking-widest text-white/40 mb-2">
          <div className="col-span-2">Code</div>
          <div className="col-span-4">Name</div>
          <div className="col-span-2">Flat €</div>
          <div className="col-span-2">Intl free min</div>
          <div className="col-span-2">Dom free min</div>
        </div>
        <div className="space-y-3">
          {airports.map((a, i) => (
            <div key={a.code} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-2 font-mono text-[#D4AF37]">{a.code}</div>
              <Input className="col-span-4 bg-[#040814] border-white/20 h-10" value={a.name} onChange={(e) => { const arr = [...airports]; arr[i] = {...a, name: e.target.value}; setAirports(arr); }} />
              <Input data-testid={`airport-flat-${a.code}`} className="col-span-2 bg-[#040814] border-white/20 h-10" type="number" step="1" value={a.flat_rate} onChange={(e) => { const arr = [...airports]; arr[i] = {...a, flat_rate: parseFloat(e.target.value)}; setAirports(arr); }} />
              <Input data-testid={`airport-intl-${a.code}`} className="col-span-2 bg-[#040814] border-white/20 h-10" type="number" step="1" value={a.free_wait_intl ?? 15} onChange={(e) => { const arr = [...airports]; arr[i] = {...a, free_wait_intl: parseFloat(e.target.value)}; setAirports(arr); }} />
              <Input data-testid={`airport-dom-${a.code}`} className="col-span-2 bg-[#040814] border-white/20 h-10" type="number" step="1" value={a.free_wait_dom ?? 10} onChange={(e) => { const arr = [...airports]; arr[i] = {...a, free_wait_dom: parseFloat(e.target.value)}; setAirports(arr); }} />
              <Button data-testid={`save-airport-${a.code}`} onClick={() => saveAirport(airports[i])} className="col-span-12 h-9 mt-1 bg-white/5 hover:bg-white/10 border border-white/15 text-xs">Save {a.code}</Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmailIntegrationTab() {
  const [s, setS] = useState({});
  const [keyDraft, setKeyDraft] = useState("");
  const [from, setFrom] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await http.get("/admin/settings");
    setS(data);
    setAdminEmail(data.admin_notify_email || "");
  };
  useEffect(() => { load(); }, []);

  const saveKey = async () => {
    if (!keyDraft.trim()) { toast.error("Paste your Resend API key first"); return; }
    if (!keyDraft.startsWith("re_")) { toast.error('Key should start with "re_"'); return; }
    setSaving(true);
    try {
      await http.put("/admin/settings", { resend_api_key: keyDraft.trim() });
      toast.success("Resend API key saved");
      setKeyDraft("");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeKey = async () => {
    if (!window.confirm("Remove the Resend API key? Email sending will stop until a new key is added.")) return;
    try {
      await http.put("/admin/settings", { resend_api_key: "" });
      toast.success("Resend key removed");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveAdminEmail = async () => {
    try {
      await http.put("/admin/settings", { admin_notify_email: adminEmail });
      toast.success("Admin email saved");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await http.post("/admin/test-email", { to: testEmail || undefined });
      toast.success(`Sent! Resend ID: ${data.id || "(no id)"} → ${data.to}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const configured = !!s.resend_api_key_set;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Left: status + config */}
      <div className="lg:col-span-2 space-y-6">
        {/* Status card */}
        <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-1">Resend Email</div>
              <h3 className="font-heading text-2xl">API key configuration</h3>
              <p className="text-sm text-white/60 mt-2 max-w-lg">
                Resend powers all transactional emails — booking confirmations to customers and new-booking alerts to admin. Emails are sent only after a booking is paid.
              </p>
            </div>
            <div data-testid="resend-status" className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-widest flex items-center gap-2 ${configured ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-yellow-500/15 text-yellow-300 border-yellow-500/30"}`}>
              {configured ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
              {configured ? "Active" : "Not configured"}
            </div>
          </div>
        </div>

        {/* API key card */}
        <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6 space-y-4">
          <div>
            <Label className="text-white/70">Resend API key</Label>
            <p className="text-xs text-white/40 mb-2">Starts with <span className="font-mono">re_</span>. Get it from <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:underline inline-flex items-center gap-1">resend.com/api-keys <ExternalLink className="w-3 h-3" /></a> · Permission needed: <b>Sending access</b>.</p>
            <div className="flex gap-2">
              <Input
                data-testid="resend-key-input"
                type="password"
                placeholder={configured ? "•••••••••••• (key already set — paste new key to replace)" : "re_AbCdEfGhI_xxxxxxxxxxxxxxxxxx"}
                value={keyDraft}
                onChange={(e) => setKeyDraft(e.target.value)}
                className="bg-[#040814] border-white/20 text-white h-12 font-mono flex-1"
              />
              <Button data-testid="resend-save-key" onClick={saveKey} disabled={saving} className="h-12 px-6 bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814] font-semibold">
                {saving ? "Saving..." : (configured ? "Replace key" : "Save key")}
              </Button>
            </div>
            {configured && (
              <button data-testid="resend-remove-key" onClick={removeKey} className="text-xs text-red-400/80 hover:text-red-400 mt-2 inline-flex items-center gap-1">
                <XCircle className="w-3 h-3" /> Remove key (disable email sending)
              </button>
            )}
          </div>

          <div className="border-t border-white/10 pt-4">
            <Label className="text-white/70">Admin notification email</Label>
            <p className="text-xs text-white/40 mb-2">Receives a copy of every paid booking.</p>
            <div className="flex gap-2">
              <Input data-testid="resend-admin-email" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@samtaxi.online" className="bg-[#040814] border-white/20 text-white h-11 flex-1" />
              <Button data-testid="resend-save-admin-email" onClick={saveAdminEmail} className="h-11 bg-white/5 hover:bg-white/10 border border-white/15">Save</Button>
            </div>
          </div>
        </div>

        {/* Test card */}
        <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Delivery test</div>
          <h3 className="font-heading text-xl mb-3">Send a test email</h3>
          <p className="text-sm text-white/60 mb-4">Send a styled Sam Taxi test email to verify Resend is configured and your domain is verified.</p>
          <div className="flex gap-2">
            <Input data-testid="resend-test-input" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder={`(defaults to ${adminEmail || "your admin email"})`} className="bg-[#040814] border-white/20 text-white h-12 flex-1" />
            <Button data-testid="resend-test-btn" onClick={sendTest} disabled={testing || !configured} className="h-12 px-6 bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814] font-semibold disabled:opacity-40">
              {testing ? "Sending..." : "Send test"}
            </Button>
          </div>
          {!configured && <p className="text-xs text-yellow-400/80 mt-2">Save an API key first.</p>}
        </div>
      </div>

      {/* Right: setup checklist */}
      <div className="space-y-6">
        <div className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Setup checklist</div>
          <ol className="space-y-3 text-sm text-white/85">
            <li className="flex gap-3">
              <span className="font-mono text-[#D4AF37] text-xs mt-0.5">01</span>
              <div>
                <a href="https://resend.com/signup" target="_blank" rel="noreferrer" className="text-white hover:text-[#D4AF37] inline-flex items-center gap-1">Create Resend account <ExternalLink className="w-3 h-3" /></a>
                <p className="text-xs text-white/50 mt-0.5">Free tier: 3,000 emails/month, 100/day.</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-[#D4AF37] text-xs mt-0.5">02</span>
              <div>
                <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-white hover:text-[#D4AF37] inline-flex items-center gap-1">Add & verify domain <ExternalLink className="w-3 h-3" /></a>
                <p className="text-xs text-white/50 mt-0.5">Add <span className="font-mono">samtaxi.online</span> → copy the SPF + DKIM DNS records to your registrar → click Verify. ~10 min DNS propagation.</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-[#D4AF37] text-xs mt-0.5">03</span>
              <div>
                <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-white hover:text-[#D4AF37] inline-flex items-center gap-1">Create API key <ExternalLink className="w-3 h-3" /></a>
                <p className="text-xs text-white/50 mt-0.5">Name: "Sam Taxi". Permission: <b>Sending access</b>. Resend shows the key ONCE — copy it.</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-[#D4AF37] text-xs mt-0.5">04</span>
              <div>
                <span className="text-white">Paste the key here</span>
                <p className="text-xs text-white/50 mt-0.5">Save it in the "API key" field on the left and click Save.</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-[#D4AF37] text-xs mt-0.5">05</span>
              <div>
                <span className="text-white">Send a test email</span>
                <p className="text-xs text-white/50 mt-0.5">Use the Delivery test card to confirm your inbox receives the styled message.</p>
              </div>
            </li>
          </ol>
        </div>

        <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6 text-xs text-white/60 space-y-2">
          <div className="font-heading text-white text-sm mb-1">Sender address</div>
          <p>Emails are sent from <span className="font-mono text-[#D4AF37]">Sam Taxi &lt;no-reply@samtaxi.online&gt;</span> by default.</p>
          <p>Before your domain is verified, you can still test with Resend's onboarding sender — they'll arrive in your inbox but may land in spam. After verification, deliverability is excellent.</p>
        </div>
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState({});
  const [pw, setPw] = useState({ email: "", new_password: "" });
  const [testEmail, setTestEmail] = useState("");
  useEffect(() => { http.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  const save = async () => {
    try { await http.put("/admin/settings", s); toast.success("Saved"); const { data } = await http.get("/admin/settings"); setS(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const updateAdmin = async () => {
    try { await http.post("/auth/update-admin", pw); toast.success("Admin updated"); setPw({ email: "", new_password: "" }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendTestEmail = async () => {
    try { const { data } = await http.post("/admin/test-email", { to: testEmail || undefined }); toast.success(`Email sent (id: ${data.id})`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6 space-y-3">
        <h3 className="font-heading text-xl mb-2">Company</h3>
        <div><Label>Brand name</Label><Input value={s.brand_name || ""} onChange={(e) => setS({...s, brand_name: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <div><Label>Phone</Label><Input value={s.phone || ""} onChange={(e) => setS({...s, phone: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <div><Label>WhatsApp (public)</Label><Input value={s.whatsapp || ""} onChange={(e) => setS({...s, whatsapp: e.target.value})} className="bg-[#040814] border-white/20 h-11" placeholder="+31648261160" /></div>
        <div><Label>Public email</Label><Input value={s.email || ""} onChange={(e) => setS({...s, email: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <div><Label>Site URL (public HTTPS, used for Mollie redirect & webhook)</Label><Input data-testid="site-url" value={s.site_url || ""} onChange={(e) => setS({...s, site_url: e.target.value})} className="bg-[#040814] border-white/20 h-11" placeholder="https://samtaxi.online" /></div>
        <div><Label>Admin notify email</Label><Input value={s.admin_notify_email || ""} onChange={(e) => setS({...s, admin_notify_email: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <Button data-testid="save-company" onClick={save} className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Save company</Button>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6 space-y-3">
        <h3 className="font-heading text-xl mb-2">Integrations & API keys</h3>
        <div><Label>Mollie API key {s.mollie_api_key_set && <span className="text-emerald-400 text-xs ml-2">[configured]</span>}</Label><Input data-testid="mollie-key" type="password" placeholder="paste new key to update" onChange={(e) => setS({...s, mollie_api_key: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        <div><Label>Resend API key {s.resend_api_key_set && <span className="text-emerald-400 text-xs ml-2">[configured]</span>}</Label><Input data-testid="resend-key" type="password" placeholder="re_xxx" onChange={(e) => setS({...s, resend_api_key: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>

        <Button data-testid="save-integrations" onClick={save} className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Save integrations</Button>

        <div className="border-t border-white/10 pt-4 mt-2 space-y-2">
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Email delivery test</div>
          <div className="flex gap-2">
            <Input data-testid="test-email-input" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="(your email — defaults to admin)" className="bg-[#040814] border-white/20 h-10 flex-1" />
            <Button data-testid="test-email-btn" onClick={sendTestEmail} className="h-10 bg-white/5 hover:bg-white/10 border border-white/15">Send test email</Button>
          </div>
          <p className="text-[11px] text-white/40">Uses currently-saved Resend key. Click "Save integrations" first if you just pasted a new key.</p>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6 space-y-3 lg:col-span-2">
        <h3 className="font-heading text-xl mb-2">Admin account</h3>
        <p className="text-sm text-white/60">Change the administrator email or password.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>New email (optional)</Label><Input data-testid="adm-new-email" value={pw.email} onChange={(e) => setPw({...pw, email: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label>New password (optional, 8+ chars)</Label><Input data-testid="adm-new-pw" type="password" value={pw.new_password} onChange={(e) => setPw({...pw, new_password: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        </div>
        <Button data-testid="update-admin-btn" onClick={updateAdmin} className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Update admin</Button>
      </div>
    </div>
  );
}

function PromosTab() {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({ code: "", discount_pct: 10, discount_amount: 0, max_uses: 0, expires_at: "", active: true });
  const load = async () => {
    try { const { data } = await http.get("/admin/promos"); setPromos(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, expires_at: form.expires_at || null };
      await http.post("/admin/promos", payload);
      toast.success("Promo created");
      setForm({ code: "", discount_pct: 10, discount_amount: 0, max_uses: 0, expires_at: "", active: true });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete promo ${code}?`)) return;
    await http.delete(`/admin/promos/${code}`);
    load();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={add} className="rounded-xl border border-white/10 bg-[#0A1128] p-6 space-y-3">
        <h3 className="font-heading text-lg mb-2">Create promo code</h3>
        <Input data-testid="promo-code" required placeholder="Code (e.g. WELCOME10)" value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} className="bg-[#040814] border-white/20 h-11 uppercase" />
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs text-white/60">Discount %</Label><Input data-testid="promo-pct" type="number" min="0" max="100" value={form.discount_pct} onChange={(e) => setForm({...form, discount_pct: parseFloat(e.target.value) || 0})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label className="text-xs text-white/60">Or fixed €</Label><Input data-testid="promo-amount" type="number" min="0" step="0.5" value={form.discount_amount} onChange={(e) => setForm({...form, discount_amount: parseFloat(e.target.value) || 0})} className="bg-[#040814] border-white/20 h-11" /></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs text-white/60">Max uses (0 = ∞)</Label><Input data-testid="promo-max" type="number" min="0" value={form.max_uses} onChange={(e) => setForm({...form, max_uses: parseInt(e.target.value) || 0})} className="bg-[#040814] border-white/20 h-11" /></div>
          <div><Label className="text-xs text-white/60">Expires (optional)</Label><Input data-testid="promo-exp" type="date" value={form.expires_at} onChange={(e) => setForm({...form, expires_at: e.target.value})} className="bg-[#040814] border-white/20 h-11" /></div>
        </div>
        <Button data-testid="promo-add" className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814]">Create</Button>
      </form>

      <div className="lg:col-span-2 rounded-xl border border-white/10 bg-[#0A1128] divide-y divide-white/10">
        {promos.map((p) => (
          <div key={p._id} className="p-5 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[#D4AF37] text-lg">{p.code}</span>
                <span className="text-sm text-white/70">{p.discount_pct ? `${p.discount_pct}% off` : `€${p.discount_amount} off`}</span>
                {!p.active && <span className="text-xs px-2 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30">disabled</span>}
              </div>
              <div className="text-white/50 text-xs mt-1">
                Used {p.used_count || 0}{p.max_uses ? `/${p.max_uses}` : ""} {p.expires_at ? `· expires ${p.expires_at.slice(0,10)}` : ""}
              </div>
            </div>
            <Button data-testid={`del-promo-${p.code}`} className="bg-red-500/15 text-red-300 hover:bg-red-500/25 h-8" onClick={() => remove(p.code)}>Delete</Button>
          </div>
        ))}
        {!promos.length && <div className="p-10 text-center text-white/50">No promo codes yet. Create one to offer discounts.</div>}
      </div>
    </div>
  );
}

function CoAdminsTab() {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lastTemp, setLastTemp] = useState(null); // { email, temp_password, email_sent }

  const load = async () => {
    try { const { data } = await http.get("/admin/managers"); setRows(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!email.trim()) { toast.error("Email required"); return; }
    try {
      setBusy(true);
      const { data } = await http.post("/admin/managers", { email: email.trim().toLowerCase(), name: name.trim(), send_invite_email: sendInvite });
      setLastTemp({ email: data.email, temp_password: data.temp_password, email_sent: data.email_sent, email_error: data.email_error });
      toast.success(`Co-admin created · ${data.email_sent ? "invite email sent" : "share the temp password manually"}`);
      setEmail(""); setName("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const resetPw = async (id, mgrEmail) => {
    try {
      const { data } = await http.post(`/admin/managers/${id}/reset-password`);
      setLastTemp({ email: mgrEmail, temp_password: data.temp_password, email_sent: false });
      toast.success("New temporary password generated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id, mgrEmail) => {
    if (!window.confirm(`Permanently remove co-admin ${mgrEmail}?`)) return;
    try {
      await http.delete(`/admin/managers/${id}`);
      toast.success("Co-admin removed");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="font-heading text-xl">Invite a co-admin (Manager)</h3>
        </div>
        <div className="space-y-3">
          <div>
            <Label>Email</Label>
            <Input data-testid="mgr-email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="partner@example.com" className="bg-[#040814] border-white/20 h-11" />
          </div>
          <div>
            <Label>Name (optional)</Label>
            <Input data-testid="mgr-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Baras" className="bg-[#040814] border-white/20 h-11" />
          </div>
          <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
            <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)}
              className="w-4 h-4 accent-[#D4AF37]" />
            Send invitation email with temporary password
          </label>
          <Button data-testid="mgr-create" onClick={create} disabled={busy}
            className="w-full h-11 bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814] font-semibold">
            {busy ? "Creating..." : "Create co-admin"}
          </Button>
          <p className="text-xs text-white/50">
            Managers can manage bookings and drivers. They <b>cannot</b> change pricing, settings, API keys or promo codes.
          </p>

          {lastTemp && (
            <div className="mt-4 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4">
              <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2">Temporary password generated</div>
              <div className="text-sm text-white/70 mb-1">For <span className="font-mono">{lastTemp.email}</span></div>
              <div className="flex items-center gap-2">
                <code data-testid="mgr-temp-pw" className="bg-[#040814] text-[#D4AF37] px-3 py-2 rounded font-mono text-lg flex-1">{lastTemp.temp_password}</code>
                <Button onClick={() => { navigator.clipboard.writeText(lastTemp.temp_password); toast.success("Copied"); }}
                  className="h-10 px-3 bg-white/10 text-white hover:bg-white/20"><Copy className="w-4 h-4" /></Button>
              </div>
              <div className="text-xs text-white/50 mt-2">
                {lastTemp.email_sent ? "✓ Invite email sent." : lastTemp.email_error ? `Email failed: ${lastTemp.email_error}` : "Email not sent — please share this temp password manually (e.g. WhatsApp)."}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0A1128] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="font-heading text-xl">Existing admins</h3>
        </div>
        <div className="space-y-2">
          {rows.map((u) => (
            <div key={u.id} data-testid={`mgr-row-${u.email}`} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-white/10 bg-[#040814]/50">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white">{u.name || u.email}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-widest ${
                    u.role === "admin" ? "border-[#D4AF37] text-[#D4AF37]" : "border-sky-400/50 text-sky-300"
                  }`}>{u.role === "admin" ? "Super Admin" : "Manager"}</span>
                  {u.must_change_password && <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-400/50 text-amber-300 uppercase tracking-widest">Pending first login</span>}
                </div>
                <div className="text-xs text-white/50 mt-0.5">{u.email}</div>
              </div>
              {u.role === "manager" && (
                <div className="flex gap-2 shrink-0">
                  <Button onClick={() => resetPw(u.id, u.email)}
                    className="h-8 px-3 text-xs bg-white/10 hover:bg-white/20 border border-white/20">
                    <KeyRound className="w-3 h-3 mr-1" /> Reset password
                  </Button>
                  <Button data-testid={`mgr-delete-${u.email}`} onClick={() => remove(u.id, u.email)}
                    className="h-8 px-3 text-xs bg-red-500/15 text-red-300 hover:bg-red-500/25">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ))}
          {!rows.length && <div className="text-center text-white/40 py-8">No admins found.</div>}
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, refresh } = useAuth();
  const isSuper = user?.role === "admin";
  const [pwOpen, setPwOpen] = useState(false);
  const [curr, setCurr] = useState("");
  const [next, setNext] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    if (user?.must_change_password) setPwOpen(true);
  }, [user?.must_change_password]);

  const submitNewPw = async (e) => {
    e?.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    try {
      setPwBusy(true);
      await http.post("/auth/change-password", { current_password: curr, new_password: next });
      toast.success("Password changed");
      setPwOpen(false); setCurr(""); setNext("");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setPwBusy(false); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 text-white">
      <Dialog open={pwOpen} onOpenChange={(v) => { if (!user?.must_change_password) setPwOpen(v); }}>
        <DialogContent className="bg-[#0A1128] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Set your new password</DialogTitle>
            <DialogDescription className="text-white/60">
              Your account uses a temporary password. Please set a new one to continue.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitNewPw} className="space-y-3">
            <div>
              <Label>Current (temporary) password</Label>
              <Input data-testid="changepw-current" type="password" value={curr} onChange={(e) => setCurr(e.target.value)}
                required className="bg-[#040814] border-white/20 h-11" />
            </div>
            <div>
              <Label>New password (min 8 chars)</Label>
              <Input data-testid="changepw-new" type="password" value={next} onChange={(e) => setNext(e.target.value)}
                required minLength={8} className="bg-[#040814] border-white/20 h-11" />
            </div>
            <Button data-testid="changepw-submit" disabled={pwBusy} type="submit"
              className="w-full h-11 bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814] font-semibold">
              {pwBusy ? "Updating..." : "Update password"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-between mb-2">
        <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]">Control Center</div>
        <div className="text-xs uppercase tracking-widest text-white/50">
          Signed in as <span className="text-white">{user?.name || user?.email}</span> ·{" "}
          <span className={`px-2 py-0.5 rounded-full border text-[10px] ${
            isSuper ? "border-[#D4AF37] text-[#D4AF37]" : "border-sky-400/50 text-sky-300"
          }`}>{isSuper ? "Super Admin" : "Manager"}</span>
        </div>
      </div>
      <h1 className="font-heading text-4xl mb-8">Admin Dashboard</h1>
      <Tabs defaultValue="bookings" className="space-y-6">
        <TabsList className="bg-[#0A1128] border border-white/10 flex-wrap">
          <TabsTrigger value="bookings" data-testid="tab-bookings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">Bookings</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="tab-drivers" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">Drivers</TabsTrigger>
          {isSuper && <TabsTrigger value="pricing" data-testid="tab-pricing" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">Pricing</TabsTrigger>}
          {isSuper && <TabsTrigger value="promos" data-testid="tab-promos" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">Promos</TabsTrigger>}
          {isSuper && <TabsTrigger value="email" data-testid="tab-email" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">Email</TabsTrigger>}
          {isSuper && <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">Settings</TabsTrigger>}
          {isSuper && <TabsTrigger value="coadmins" data-testid="tab-coadmins" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">Co-admins</TabsTrigger>}
        </TabsList>
        <TabsContent value="bookings"><BookingsTab /></TabsContent>
        <TabsContent value="drivers"><DriversTab /></TabsContent>
        {isSuper && <TabsContent value="pricing"><PricingTab /></TabsContent>}
        {isSuper && <TabsContent value="promos"><PromosTab /></TabsContent>}
        {isSuper && <TabsContent value="email"><EmailIntegrationTab /></TabsContent>}
        {isSuper && <TabsContent value="settings"><SettingsTab /></TabsContent>}
        {isSuper && <TabsContent value="coadmins"><CoAdminsTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
